// Build identity for the public brand surfaces (Brief 5, point 6): the app
// version, plus the environment label shown only when NOT production. The
// version is sourced from waytide-web's package.json via Vite's `__APP_VERSION__`
// define (vite.config.ts), so it tracks the release with no env wiring.
//
// The environment is derived from two signals so it's correct in both CI builds
// and local/dev: VITE_DEPLOY_ENV (set by the deploy workflow) and VITE_API_URL
// (the backend origin — "staging" appears in the staging API host). Anything
// that isn't production gets a small muted badge; production shows nothing.

/** The release version, e.g. "0.2.0". Baked in at build time from package.json. */
export const APP_VERSION: string =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0";

export type EnvSignals = {
  /** VITE_DEPLOY_ENV — the deploy workflow names the environment here. */
  deployEnv?: string;
  /** VITE_API_URL — the backend origin; the staging host contains "staging". */
  apiUrl?: string;
};

/**
 * Resolve the environment badge label, or `null` when nothing should show.
 *
 * Rules (pure, no globals):
 *  - production (VITE_DEPLOY_ENV === "production") → null (show only the version).
 *  - an explicit non-production VITE_DEPLOY_ENV (e.g. "staging") → that label.
 *  - otherwise, if VITE_API_URL points at a staging host → "staging".
 *  - a bare local/dev build with no signals → null (keep the landing clean).
 */
export function environmentLabel({ deployEnv, apiUrl }: EnvSignals): string | null {
  const env = deployEnv?.trim().toLowerCase();
  if (env === "production" || env === "prod") return null;
  if (env && env !== "local" && env !== "development" && env !== "dev") {
    return env;
  }
  if ((apiUrl ?? "").toLowerCase().includes("staging")) return "staging";
  return null;
}

/** The live environment label from the build's env, or null in production. */
export function currentEnvironmentLabel(): string | null {
  const env = import.meta.env as Record<string, string | undefined>;
  return environmentLabel({
    deployEnv: env.VITE_DEPLOY_ENV,
    apiUrl: env.VITE_API_URL,
  });
}
